<template>
    <div class="import-record-form">
        <a-steps v-model="current" type="navigation" size="small" :style="stepStyle">
            <a-step
                :title="$t('wp.selectContactFile')"
                :description="$t('wp.selectContactCsvOrExcel')"
                :status="current === 0 ? 'process' : 'wait'"
            />
            <a-step
                :title="$t('wp.importConfiguration')"
                :description="$t('wp.customizeFile')"
                :status="current === 1 ? 'process' : 'wait'"
            />
        </a-steps>
        <div v-if="current === 0" class="fileSelection">
            <div class="file-box" @click="openSelectFile">
                <div class="">
                    <a-icon type="file-excel" style="font-size: 50px; color: #08c" />
                </div>
                <div class="file-details">
                    <span v-if="file">{{ file.name }}</span>
                    <span>{{ $t("wp.selectFile") }}</span>
                    <span>{{ $t("wp.csvOrExcelFile") }}</span>
                </div>
            </div>
        </div>
        <div v-if="current === 1" class="configuration">
            <div class="left">
                <section class="general">
                    <div class="field">
                        <label> {{ $t("tables.firstRowHeader") }} ? </label>
                        <a-switch v-model="firstRowContainsHeader" />
                    </div>

                    <div class="field" v-if="!excelMode">
                        <label> {{ $t("message.separatorCharacter") }} </label>
                        <a-select v-model="seperator">
                            <a-select-option value=",">{{ $t("tables.comma") }} (,)</a-select-option>
                            <a-select-option value=";">{{ $t("tables.semicolan") }} (;)</a-select-option>
                        </a-select>
                    </div>
                </section>
                <a-divider />
                <section class="attributes">
                    <label>{{ $t("tables.tableFieldOrFileColumns") }}</label>
                    <div class="attributes-items">
                        <div
                            class="attribute"
                            v-for="field in table.fields"
                            :key="field._id"
                            v-if="isValidField(field)"
                        >
                            <div class="field">
                                <label>{{ field.name }}</label>
                            </div>
                            <div class="field">
                                <a-select v-model="fieldColumnMap[field._id]" :options="getSelectOptionsForColumns()" />
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <div class="right">
                <section class="result">
                    <label>{{ $t("tables.exampleResultPreview") }}</label>
                    <a-table
                        :columns="getTableDataSource().columns"
                        :data-source="getTableDataSource().rows"
                        :pagination="false"
                        :style="{ width: '100%' }"
                    />
                </section>

                <section class="footer">
                    <a-button @click="onCancelled">{{ $t("message.cancel") }}</a-button>
                    <a-button type="primary" @click="save" :loading="importing">
                        {{ importing ? "Importing" : $t("message.importRecords") }}
                    </a-button>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../utils/log2";
import { generateRandomId } from "../../../../../utils/session";
import _ from "lodash";
import readXlsxFile from "read-excel-file";

const log = log2("ImportContactsFromFile");
export default {
    name: "ImportRecords",
    props: {
        onCompleted: {
            type: Function,
            required: true,
        },
        onCancelled: {
            type: Function,
            required: true,
        },
        table: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            current: 0,
            stepStyle: {
                marginBottom: "20px",
            },
            file: null,
            headers: [],
            columns: [],
            firstRowContainsHeader: true,
            seperator: ",",
            fileText: "",
            excelMode: false,
            importing: false,
            autoFixForTurkey: false,
            fieldColumnMap: {},
        };
    },
    methods: {
        isValidField(field) {
            return (
                field.fieldType !== "formula" &&
                !field.fieldType.startsWith("record_") &&
                field.fieldType !== "auto-increment" &&
                field.fieldType !== "lookup"
            );
        },
        save() {
            this.importing = true;
            const mappedRecords = this.getMappedRecords();
            this.$emit("import-records", mappedRecords);
        },
        getAttributeHeaders() {
            log("getAttributeHeaders", this.headers);
            return this.headers;
        },
        getSelectOptionsForColumns() {
            return this.headers.map((header) => {
                return {
                    label: header.name,
                    value: header.uniqueId,
                };
            });
        },
        openSelectFile() {
            let fileEl = document.createElement("input");
            fileEl.setAttribute("type", "file");
            fileEl.setAttribute(
                "accept",
                ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            );
            fileEl.onchange = async () => {
                let file = fileEl.files[0];
                log("selected file", file);
                const isExcel = !file.type.includes("text/csv");
                if (file) {
                    this.file = file;
                    log(file);
                    if (isExcel) {
                        readXlsxFile(file).then((rows) => {
                            this.lines = rows;
                            this.parseExcel(rows);
                            this.excelMode = true;
                            this.current = 1;
                        });
                    } else {
                        const text = await this.file.text();
                        this.fileText = text;
                        this.parseCSVText(text);
                        this.current = 1;
                        this.excelMode = false;
                    }
                } else {
                    this.file = null;
                }
            };
            fileEl.click();
        },
        toVariableName(name) {
            return name
                .toString()
                .trim()
                .toLowerCase()
                .split(" ")
                .map((x) => x.trim().replace(/[^a-z0-9_]/g, ""))
                .filter((x) => x.length > 0)
                .join("-");
        },
        parseCSVText(text) {
            let lines = text.split("\n");
            let result = [];
            this.headers = [];
            this.columns = [];
            this.headers = lines[0].split(this.seperator).map((name, index) => {
                const _name = this.firstRowContainsHeader ? name : `Column ${index + 1}`;
                const id = this.toVariableName(_name);
                return {
                    name: _name,
                    index,
                    id,
                    uniqueId: generateRandomId(4),
                };
            });
            this.columns = this.headers.map((h, i) => {
                return {
                    attribute: h.id,
                    defaultValue: "",
                    headerUniqueId: h.uniqueId,
                    index: i,
                };
            });
            this.lines = lines;
        },
        parseExcel(lines) {
            this.headers = [];
            this.columns = [];
            this.headers = lines[0].map((name, index) => {
                const _name = this.firstRowContainsHeader ? name : `Column ${index + 1}`;
                const id = this.toVariableName(_name);
                return {
                    name: _name,
                    index,
                    id,
                    uniqueId: generateRandomId(4),
                };
            });
            this.columns = this.headers.map((h, i) => {
                return {
                    attribute: h.id,
                    defaultValue: "",
                    headerUniqueId: h.uniqueId,
                    index: i,
                };
            });
            this.lines = lines;
        },
        getTableDataSource() {
            const lines = this.firstRowContainsHeader ? this.lines.slice(1) : this.lines;

            const columns = this.table.fields.map((field) => ({
                title: field.name,
                dataIndex: field._id,
                key: field._id,
            }));
            const cols = this.columns;
            const rows = _.take(lines, 5).map((line, index) => {
                const values = this.excelMode ? line : line.split(this.seperator);

                const row = {};
                log("line debug", {
                    cols,
                    columns,
                    values,
                    line,
                    lines,
                    fieldColumnMap: this.fieldColumnMap,
                });
                this.table.fields.forEach((field) => {
                    const columnId = this.fieldColumnMap[field._id];
                    if (!columnId) return "";
                    const col = cols.find((x) => x.headerUniqueId === columnId);
                    row[field._id] = values[col.index];
                });
                return row;
            });

            return {
                columns,
                rows,
            };
        },
        getMappedRecords() {
            const lines = this.firstRowContainsHeader ? this.lines.slice(1) : this.lines;
            const cols = this.columns;

            return lines.map((line) => {
                const values = this.excelMode ? line : line.split(this.seperator);
                const record = {};

                this.table.fields.forEach((field) => {
                    const columnId = this.fieldColumnMap[field._id];
                    if (columnId) {
                        const col = cols.find((x) => x.headerUniqueId === columnId);
                        record[field._id] = values[col.index];
                    }
                });

                return record;
            });
        },
    },
    watch: {
        seperator() {
            if (!this.excelMode) this.parseCSVText(this.fileText);
        },
        firstRowContainsHeader() {
            if (this.excelMode) this.parseExcel(this.lines);
            else this.parseCSVText(this.fileText);
        },
    },
};
</script>

<style lang="scss">
.import-record-form {
    .alert-message {
        display: flex;
        flex-direction: column;
        gap: 10px;
        background: #fff3cd; /* Light yellow background */
        color: #856404; /* Dark yellow text color */
        padding: 10px;
        border-radius: 4px;
    }

    .navs {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .fileSelection {
        display: flex;
        justify-content: center;
        align-items: center;

        .file-box {
            width: 100%;
            height: 200px;
            border: 1px solid #d9d9d9;
            border-radius: 4px;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            width: 300px;
            background: azure;

            .file-details {
                display: flex;
                flex-direction: column;
                gap: 10px;
            }
        }
    }

    .configuration {
        display: flex;
        gap: 10px;

        .left {
            width: 50%;
        }

        .right {
            width: 50%;
            border-left: 1px solid #d9d9d9;
            padding-left: 10px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            section.footer {
                display: flex;
                justify-content: flex-end;
                gap: 10px;
            }
        }

        section.general {
            display: flex;
            gap: 20px;

            .field {
                display: flex;
                flex-direction: column;

                label {
                    font-size: 16px;
                    color: #232222;
                    font-weight: 600;
                }

                .ant-switch {
                    width: 10px;
                }
            }
        }

        section.number {
            display: flex;
            flex-direction: column;
            gap: 10px;

            label {
                font-size: 16px;
                color: #232222;
                font-weight: 600;
            }

            .fields {
                display: flex;
                gap: 20px;

                .field {
                    display: flex;
                    flex-direction: column;
                    min-width: 200px;

                    label {
                        font-size: 16px;
                        color: #232222;
                        font-weight: 600;
                    }

                    .ant-switch {
                        width: 10px;
                    }
                }
            }
        }

        section.attributes {
            display: flex;
            flex-direction: column;
            gap: 20px;

            label {
                font-size: 16px;
                color: #232222;
                font-weight: 600;
            }

            .attributes-items {
                display: flex;
                flex-direction: column;
                gap: 10px;

                .attribute {
                    display: flex;
                    gap: 20px;

                    .field {
                        display: flex;
                        flex-direction: column;
                        min-width: 200px;

                        label {
                            font-size: 16px;
                            color: #232222;
                            font-weight: 600;
                        }

                        .ant-switch {
                            width: 10px;
                        }
                    }
                }
            }
        }

        section.result {
            display: flex;
            flex-direction: column;
            gap: 20px;
            overflow-y: auto;

            label {
                font-size: 16px;
                color: #232222;
                font-weight: 600;
            }
        }
    }
}
</style>
